import SDKMessageBus from '../../utility/message-bus';
import promiseFromEvent from '../../utility/promiseFromEvent';
import { Maybe } from 'maybe-not';

const defaultIconStyles: string = `
:root {
    --t-icon-stroke-width: 1.5;
    --t-icon-cap: round;
    --t-icon-stroke-primary: currentColor;
    --t-icon-stroke-secondary: var(--t-icon-stroke-primary);
    --t-icon-fill: none;
}`;

export default function setupQ2Styles(
    messageBus: SDKMessageBus,
    moduleInstanceId: string,
    capabilities: Set<string>,
    resizeIframe: () => void
): Promise<() => void> {
    if (moduleInstanceId !== 'legacy') {
        document.body.dataset.tectonModule = '';
    }
    const currentThemePathPromise = capabilities.has('ThemesNotSupported')
        ? Promise.resolve(Maybe.nothing<string>())
        : messageBus
              .sendRequest<{ path: string }>('getCurrentTheme', Maybe.nothing<string>(), {
                  timeout: 500
              })
              .then(resp => Maybe.just<string>(resp.value.path))
              .catch(err => Maybe.nothing<string>());

    return Promise.all([
        messageBus.sendRequest<{ path: string }>(
            'getBaseAssetPath',
            Maybe.just({ subPath: 'tecton.css' }).map(JSON.stringify)
        ),
        messageBus.sendRequest<{ path: string }>(
            'getBaseAssetPath',
            Maybe.just({ subPath: 'tecton-props.css' }).map(JSON.stringify)
        ),
        currentThemePathPromise
    ]).then(([tectonBaseCSSPathResponse, tectonPropsCSSPathResponse, themeMaybe]) => {
        // ORDER matters!! add base and then theme
        return Promise.all([
            fetchStyleTextfromURL(tectonPropsCSSPathResponse.value.path),
            themeMaybe
                .asyncMap(themePath => fetchStyleTextfromURL(themePath))
                .then(val => Maybe.just<string>(val))
                .catch(err => Maybe.nothing<string>()),
            promiseFromEvent(addLinkTag(tectonBaseCSSPathResponse.value.path), 'load')
        ]).then(([propsCssText, maybeThemeCssText]) => {
            addToShadyCSS(createStyleTag('tecton-props-css', propsCssText), resizeIframe);

            const maybeThemeChangeSubscription = maybeThemeCssText.map(themeCssText => {
                addToShadyCSS(createStyleTag('tecton-theme-css', themeCssText), resizeIframe);

                if (!themeCssText.includes('--t-icon-stroke')) {
                    addToShadyCSS(
                        createStyleTag('tecton-default-icon-css', defaultIconStyles),
                        resizeIframe
                    );
                }

                return messageBus
                    .onBroadcast<{ path: string }>('themeChanged')
                    .subscribe(themePathResponse => {
                        fetchStyleTextfromURL(themePathResponse.value.path)
                            .then((text: string) => {
                                return {
                                    styleTag: createStyleTag('tecton-theme-css', text),
                                    resizeIframe
                                };
                            })
                            .then(({ styleTag, resizeIframe }) =>
                                addToShadyCSS(styleTag, resizeIframe)
                            );
                    });
            });

            return function() {
                maybeThemeChangeSubscription.map(sub => sub.unsubscribe());
            };
        });
    });
}

export function fetchStyleTextfromURL(url: string): Promise<string> {
    return fetch(url).then(response => {
        if (!response.ok) {
            throw new Error(response.statusText);
        }
        return response.text();
    });
}

export function createStyleTag(id: string, text: string): HTMLStyleElement {
    const styleEl = document.createElement('style');
    styleEl.textContent = text;
    styleEl.id = id;

    return styleEl;
}

export function addToShadyCSS(style: HTMLStyleElement, resizeIframe: () => void): void {
    const currentStyleTag = document.getElementById(style.id);

    if (currentStyleTag) {
        currentStyleTag.parentNode!.replaceChild(style, currentStyleTag);
    } else {
        document.querySelector('head')!.appendChild(style);
    }

    // after styles are inserted trigger iframe resize
    resizeIframe();
}

function addLinkTag(path: string) {
    const headTag = document.querySelector('head')!;
    const linkTag = document.createElement('link');

    linkTag.setAttribute('href', path);
    linkTag.setAttribute('rel', 'stylesheet');

    headTag.appendChild(linkTag);

    return linkTag;
}
